import {
  markRaw,
  ref,
  type Component,
  type ComponentPublicInstance,
  type VNodeProps,
  type AllowedComponentProps,
  type ComponentCustomProps,
} from 'vue'

type VueComponent = new () => ComponentPublicInstance<any, any, any, any, any>

// Extract props from a component instance
type ExtractInstanceProps<T extends VueComponent> = Omit<
  InstanceType<T>['$props'],
  keyof VNodeProps | keyof AllowedComponentProps | keyof ComponentCustomProps
>

type QueuedModal = {
  id: number
  component: Component
  props: any
}

const modalQueue = ref<QueuedModal[]>([])
const idCounter = ref(0)

export function useModal() {
  const openModal = <T extends VueComponent, ResolveType = boolean | undefined>(
    component: T,
    props: Partial<ExtractInstanceProps<T>>,
  ) => {
    return new Promise<ResolveType>((resolve, reject) => {
      const rawComponent = markRaw(component)
      const id = idCounter.value++

      const modalProps = {
        id,
        resolve: (data: ResolveType) => {
          resolve(data)
        },
        reject: (data: boolean | undefined) => {
          reject(data)
        },
        ...props,
      }

      modalQueue.value?.push({
        component: rawComponent,
        props: modalProps,
        id,
      })
    })
  }

  const closeModal = (id: number) => {
    const modalIndex = modalQueue.value.findIndex((modal) => modal.id === id)
    modalQueue.value.splice(modalIndex, 1)
  }

  return {
    modalQueue,
    openModal,
    closeModal,
  }
}
